import { useEffect, useMemo, useRef, useState } from "react";

// Interfaces
import { ITier } from "@/Common/helpers/Matrices/interfaces";
import { IElrHroBonusContent, IElrHroBonusProps, IElrHroContentData, TElrHroBonusModel, TTierBonus } from "./interfaces";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { getErrorKey } from "@/Common/helpers/Modifiers/characters";
import { getGlobalDisabled, getTabs, getTooltips } from "./helpers";
import { isLastValid, isValidMatrixRange } from "@/Common/helpers/Matrices/tiers";

// Hooks
import { detachValidate, isValidMapErrors } from "@/Common/hooks/Validations";

// Layouts
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";

// MUI
import { Grid } from "@mui/material";
import ElrHroBonusVariant from "./ElrHroBonusVariant";
import { validateElrHroTiers } from "./validations-fn";

const getPotentialTabsErrorsKeys = (section: string, tabs: { key: string }[] = []) => tabs?.map((tab) => getErrorKey(section, tab?.key));
const getTabIdentifier = (model: TElrHroBonusModel | null, index: number) => model?.tabs?.[index].key ?? "";
const getUpdatedModel = (data: TElrHroBonusModel) => {
	const { content } = data;
	return content;
};

const isTierConditionsValid = (tiers: TTierBonus[] = []) => {
	const isLastBonusValid = isLastValid(tiers as unknown as ITier[], { column: "bonuses", reference: "bonus" });
	const isMatrixInRange = isValidMatrixRange(tiers as unknown as ITier[], { column: "bonuses", reference: "bonus" });
	return isLastBonusValid && isMatrixInRange;
};

const isSomeConfiguredModel = (content?: IElrHroBonusContent) => {
	const { customer, internal, warranty } = content ?? {};
	const makes = [customer, internal, warranty];
	return makes?.some((item) => isTierConditionsValid(item?.tiers ?? []));
};

const validateBeforeSend = (model?: IElrHroContentData, isSomeConfigured = false) => {
	const { tiers = [], option = "gross" } = model ?? {};
	const validationMethod = validateElrHroTiers(tiers, option, isSomeConfigured);
	const validation = detachValidate(validationMethod);
	return isValidMapErrors(validation);
};

const isAllValid = (content?: IElrHroBonusContent, isSomeConfigured = false) => {
	const { customer, internal, warranty } = content ?? {};
	const makes = [customer, internal, warranty];
	const errors = makes.map((model) => validateBeforeSend(model, isSomeConfigured));
	return !errors.length || [...errors].every((error) => error);
};

export default function ElrHroExtension(props: Readonly<IElrHroBonusProps>) {
	const { sectionId, content, variant, loading = false, readOnly = false, onActionUpdate, useSectionsErrors } = props;

	const tabsDefinition = getTabs();

	// Hooks
	const { errors, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() ?? {};

	// References
	const saveErrors = useRef<string[]>([]);

	// States
	const [tabIndex, setTabIndex] = useState(0);
	const [model, setModel] = useState<TElrHroBonusModel | null>(null);
	const tabIdentifier = getTabIdentifier(model, tabIndex);
	const isGlobalDisabled = getGlobalDisabled(model, tabIdentifier);
	const isSomeModelConfigured = isSomeConfiguredModel(model?.content);

	// Error handler
	const tabsErrors = useMemo(() => {
		if (errors.size && model?.tabs) {
			const localErrors = Array.from(Array(model.tabs.length)).map(() => false);
			const errorsToKeep = getPotentialTabsErrorsKeys(sectionId, model.tabs);
			const listErrors = errors?.get(sectionId) ?? []; // Ids that have errors

			errorsToKeep.forEach((errorKey, index) => {
				const included = listErrors.includes(errorKey) ?? saveErrors?.current?.includes(errorKey);
				localErrors[index] = Boolean(included);
			});
			return localErrors;
		}
		return [];
	}, [sectionId, errors, model]);

	// Actions handlers
	const update = (data: TElrHroBonusModel) => {
		const isSomeDefined = isSomeConfiguredModel(data?.content);
		const isValidModel = isAllValid(data?.content, isSomeDefined);
		const isValid = isValidModel && isSomeDefined;
		if (isValid && Boolean(model)) {
			const modelToSave = getUpdatedModel(data);
			onActionUpdate?.(sectionId, null, modelToSave, isValid);
		}
		setModel(data);
	};

	const handleUpdateSwitch = (area: string, value: boolean) => {
		const updatedModel = { ...model };
		const updatedContent = { ...model?.content } as IElrHroBonusContent;
		if (updatedContent[tabIdentifier as keyof Omit<IElrHroBonusContent, "_id">] && (area === "relativeToStore" || area === "global")) {
			updatedContent[tabIdentifier as keyof Omit<IElrHroBonusContent, "_id">][area] = value;
		}
		update({ ...updatedModel, content: updatedContent });
	};

	const handleUpdate = (section: string, area: string, data: IElrHroContentData) => {
		const updatedModel = { ...model };
		const updatedContent = { ...model?.content } as IElrHroBonusContent;
		removeSectionError?.(section, area); // Remove section error
		updatedContent[area as keyof Omit<IElrHroBonusContent, "_id">] = { ...data };
		update({ ...updatedModel, content: updatedContent });
	};

	const handleValidation = (section: string, area: string, value: boolean) => {
		value ? removeSectionError?.(section, area) : addSectionError?.(section, area);
	};

	useEffect(() => {
		return () => {
			clearSectionErrors?.(sectionId);
		};
	}, [sectionId, clearSectionErrors]);

	useEffect(() => {
		setModel({
			content: clone(content),
			tabs: tabsDefinition,
		});

		return () => {
			setModel(null);
		};
	}, [content]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader tabs={3} />
						<DefaultTiersLoader />
					</Grid>
				) : (
					Boolean(model) && (
						<TabSelectable
							tabs={tabsDefinition?.map(({ tabName }) => tabName)}
							tabSelected={tabIndex}
							onTabChange={(_, information) => setTabIndex(information?.index)}
							errors={tabsErrors}
							content={Object.entries(tabsDefinition)?.map(([key, { key: tabKey }], index) => {
								const gridKey = `${sectionId}_${key}_${index}`;
								return (
									<Grid container key={gridKey}>
										<Grid item xs={12} className={"models-tiered-area"}>
											<ElrHroBonusVariant
												identifier={tabKey}
												variant={variant}
												relativeToStore={Boolean(
													model?.content?.[tabIdentifier as keyof Omit<IElrHroBonusContent, "_id">]
														?.relativeToStore
												)}
												content={model?.content?.[tabKey as keyof Omit<IElrHroBonusContent, "_id">]}
												tooltips={getTooltips(variant)}
												isSomeConfigured={isSomeModelConfigured}
												readOnly={readOnly}
												onActionUpdate={(area: string, updatedModel: IElrHroContentData) =>
													handleUpdate(sectionId, area, updatedModel)
												}
												onActionValidation={(area: string, isValid: boolean) =>
													handleValidation(sectionId, area, isValid)
												}
											/>
										</Grid>
									</Grid>
								);
							})}
							bottomActions={[
								<CustomizedSwitches
									key={`${sectionId}_${tabIdentifier}_relative_switch`}
									textChecked="Relative to Store"
									textUnChecked="Relative to Self"
									checked={Boolean(
										model?.content?.[tabIdentifier as keyof Omit<IElrHroBonusContent, "_id">]?.relativeToStore
									)}
									onChange={(value) => handleUpdateSwitch("relativeToStore", value)}
									disabled={readOnly}
									tooltip={{
										key: `${sectionId}_${tabIdentifier}_relative_tooltip`,
										title: "Measures the efficiency relative to personal effort or the entire store.",
										placement: "bottom",
									}}
								/>,
								<CustomizedSwitches
									key={`${sectionId}_${tabIdentifier}_global_switch`}
									disabled={readOnly || isGlobalDisabled}
									textChecked="Global"
									textUnChecked="Global"
									checked={Boolean(model?.content?.[tabIdentifier as keyof Omit<IElrHroBonusContent, "_id">]?.global)}
									onChange={(value) => handleUpdateSwitch("global", value)}
									tooltip={{
										key: `${sectionId}_${tabIdentifier}_global_tooltip`,
										title: "Apply globally",
										placement: "bottom",
									}}
								/>,
							]}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
